/**
 * History Modal
 * 
 * 평가 히스토리 목록을 표시하는 모달
 * - 개인 탭: team_id=NULL인 개인 히스토리
 * - 회사별 탭: team_id별 히스토리 (가로 스크롤)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage, formatDate, parseUTCDate } from '../i18n';
import { useBodyScrollLock } from '../hooks';
import { getHistory, getHistoryTabs, type HistoryItem, type QuotaInfo, type HistoryTabInfo } from '../api';
import type { ReleaseStatus } from '../types';

interface HistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectItem: (id: number) => void;
  quota?: QuotaInfo | null;
  onUpgrade?: () => void;
}

// 상태별 배지 스타일
const getStatusBadge = (status: string | null) => {
  const statusKey = (status || 'unknown') as ReleaseStatus | 'unknown';
  const configs: Record<string, { bg: string; text: string; label: string }> = {
    go: { bg: 'bg-status-go/20', text: 'text-status-go', label: 'GO' },
    conditional: { bg: 'bg-status-warning/20', text: 'text-status-warning', label: 'COND' },
    holding: { bg: 'bg-status-holding/20', text: 'text-status-holding', label: 'HOLD' },
    no_go: { bg: 'bg-status-danger/20', text: 'text-status-danger', label: 'NO-GO' },
    unknown: { bg: 'bg-terminal-dim/20', text: 'text-terminal-muted', label: '-' },
  };
  return configs[statusKey] || configs.unknown;
};

// 점수별 색상
const getScoreColor = (score: number | null) => {
  if (score === null) return 'text-terminal-muted';
  if (score >= 80) return 'text-status-go';
  if (score >= 60) return 'text-status-warning';
  if (score >= 40) return 'text-status-holding';
  return 'text-status-danger';
};

export default function HistoryModal({ isOpen, onClose, onSelectItem, quota: _quota, onUpgrade: _onUpgrade }: HistoryModalProps) {
  const { lang, t } = useLanguage();
  
  // 탭 상태
  const [tabs, setTabs] = useState<HistoryTabInfo[]>([]);
  const [activeTabId, setActiveTabId] = useState<number | null>(null); // null = 개인
  const [isTabsLoading, setIsTabsLoading] = useState(false);
  
  // 히스토리 상태
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [_limit, setLimit] = useState(0); // 추후 사용 예정
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // 탭 스크롤 상태
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  
  const PER_PAGE = 5;
  const isKo = lang === 'ko';

  // 탭 스크롤 상태 업데이트
  const updateScrollArrows = useCallback(() => {
    const container = tabsContainerRef.current;
    if (!container) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = container;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  // 탭 스크롤 핸들러
  const scrollTabs = (direction: 'left' | 'right') => {
    const container = tabsContainerRef.current;
    if (!container) return;
    
    const scrollAmount = 150;
    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  };

  // 탭 목록 로드
  const loadTabs = useCallback(async () => {
    setIsTabsLoading(true);
    try {
      const response = await getHistoryTabs();
      setTabs(response.tabs);
      
      // 첫 번째 탭 선택 (개인 또는 첫 번째 팀)
      if (response.tabs.length > 0) {
        setActiveTabId(response.tabs[0].team_id);
      }
    } catch (err) {
      console.error('Failed to load history tabs:', err);
    } finally {
      setIsTabsLoading(false);
    }
  }, []);

  // 히스토리 데이터 로드
  const loadHistory = useCallback(async (page: number = 1, teamId: number | null = activeTabId) => {
    setIsLoading(true);
    setError(null);
    try {
      // teamId가 null이면 개인, 숫자면 해당 팀
      const response = await getHistory(page, PER_PAGE, teamId ?? undefined);
      setItems(response.items);
      setTotal(response.total);
      setLimit(response.limit);
      setCurrentPage(response.page);
      setTotalPages(response.total_pages);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.history.loadError);
    } finally {
      setIsLoading(false);
    }
  }, [activeTabId, t.history.loadError]);

  // 모달 열릴 때 탭 로드
  useEffect(() => {
    if (isOpen) {
      loadTabs();
    }
  }, [isOpen, loadTabs]);

  // 탭 변경 시 히스토리 로드
  useEffect(() => {
    if (isOpen && !isTabsLoading && tabs.length > 0) {
      setCurrentPage(1);
      loadHistory(1, activeTabId);
    }
  }, [isOpen, isTabsLoading, tabs.length, activeTabId, loadHistory]);

  // 스크롤 이벤트 리스너
  useEffect(() => {
    const container = tabsContainerRef.current;
    if (!container) return;
    
    updateScrollArrows();
    container.addEventListener('scroll', updateScrollArrows);
    window.addEventListener('resize', updateScrollArrows);
    
    return () => {
      container.removeEventListener('scroll', updateScrollArrows);
      window.removeEventListener('resize', updateScrollArrows);
    };
  }, [tabs, updateScrollArrows]);
  
  // 페이지 변경 핸들러
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      loadHistory(page, activeTabId);
    }
  };

  // 탭 선택 핸들러
  const handleTabSelect = (teamId: number | null) => {
    setActiveTabId(teamId);
  };

  // 현재 탭 이름 가져오기
  const getCurrentTabName = () => {
    const tab = tabs.find(t => t.team_id === activeTabId);
    if (!tab) return '';
    if (tab.team_id === null) return isKo ? '개인' : 'Personal';
    return tab.team_name;
  };

  // 모달 열릴 때 배경 스크롤 방지
  useBodyScrollLock(isOpen);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-terminal-bg border border-terminal-border rounded-xl shadow-2xl w-full max-w-2xl h-[600px] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="flex-shrink-0 border-b border-terminal-border px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-terminal-text flex items-center gap-2">
              📜 {t.history.title}
            </h2>
            <p className="text-sm text-terminal-muted mt-1">
              {getCurrentTabName()} - {isKo ? `총 ${total}건` : `${total} items`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-terminal-dim/30 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-terminal-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* 탭 (스크롤 가능) */}
        {tabs.length > 0 && (
          <div className="flex-shrink-0 border-b border-terminal-border relative">
            {/* 왼쪽 화살표 */}
            {showLeftArrow && (
              <button
                onClick={() => scrollTabs('left')}
                className="absolute left-0 top-0 bottom-0 z-10 px-2 bg-gradient-to-r from-terminal-bg via-terminal-bg to-transparent flex items-center"
              >
                <svg className="w-4 h-4 text-terminal-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            
            {/* 탭 컨테이너 */}
            <div
              ref={tabsContainerRef}
              className="flex overflow-x-auto scrollbar-hide px-6 gap-1"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {tabs.map((tab) => (
                <button
                  key={tab.team_id ?? 'personal'}
                  onClick={() => handleTabSelect(tab.team_id)}
                  className={`px-4 py-3 text-sm font-medium transition-colors relative whitespace-nowrap flex-shrink-0 ${
                    activeTabId === tab.team_id
                      ? 'text-status-go'
                      : 'text-terminal-muted hover:text-terminal-text'
                  }`}
                >
                  {tab.team_id === null ? (isKo ? '👤 개인' : '👤 Personal') : `🏢 ${tab.team_name}`}
                  <span className="ml-1 text-xs opacity-70">({tab.count})</span>
                  {activeTabId === tab.team_id && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-status-go" />
                  )}
                </button>
              ))}
            </div>
            
            {/* 오른쪽 화살표 */}
            {showRightArrow && (
              <button
                onClick={() => scrollTabs('right')}
                className="absolute right-0 top-0 bottom-0 z-10 px-2 bg-gradient-to-l from-terminal-bg via-terminal-bg to-transparent flex items-center"
              >
                <svg className="w-4 h-4 text-terminal-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </div>
        )}

        {/* 본문 */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* 로딩 */}
          {(isLoading || isTabsLoading) && (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-3 border-status-go/30 border-t-status-go rounded-full animate-spin"></div>
            </div>
          )}

          {/* 에러 */}
          {error && !isLoading && (
            <div className="p-4 bg-status-danger/10 border border-status-danger/30 rounded-lg text-status-danger text-sm text-center">
              {error}
              <button
                onClick={() => loadHistory(currentPage, activeTabId)}
                className="block mx-auto mt-2 text-xs underline hover:no-underline"
              >
                {t.history.retry}
              </button>
            </div>
          )}

          {/* 빈 상태 */}
          {!isLoading && !isTabsLoading && !error && items.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="text-4xl mb-4">{activeTabId === null ? '📋' : '🏢'}</div>
              <p className="text-terminal-muted">
                {activeTabId === null 
                  ? t.history.empty
                  : (isKo ? '팀 히스토리가 없습니다' : 'No team history')
                }
              </p>
              {activeTabId !== null && (
                <p className="text-xs text-terminal-dim mt-2">
                  {isKo ? '이 팀에서 생성한 평가가 여기에 표시됩니다' : 'Evaluations created in this team will appear here'}
                </p>
              )}
            </div>
          )}

          {/* 히스토리 목록 */}
          {!isLoading && !isTabsLoading && !error && items.length > 0 && (
            <div className="space-y-3">
              {items.map((item) => {
                const statusBadge = getStatusBadge(item.status);
                const scoreColor = getScoreColor(item.confidence_score);
                const createdDate = formatDate(parseUTCDate(item.created_at), lang);
                
                return (
                  <button
                    key={item.id}
                    onClick={() => onSelectItem(item.id)}
                    className="w-full text-left p-4 bg-terminal-dim/10 hover:bg-terminal-dim/20 border border-terminal-border rounded-lg transition-colors group"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-terminal-text truncate group-hover:text-status-go transition-colors">
                          {item.feature_name}
                        </h3>
                        <div className="flex items-center gap-3 mt-1 text-sm text-terminal-muted">
                          <span className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            {item.release_date}
                          </span>
                          <span className="text-terminal-dim">•</span>
                          <span className="text-xs text-terminal-dim">{createdDate}</span>
                          {/* 팀 히스토리에서 생성자 표시 */}
                          {activeTabId !== null && item.user_name && (
                            <>
                              <span className="text-terminal-dim">•</span>
                              <span className="text-xs text-terminal-dim flex items-center gap-1">
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                {item.user_name}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <div className={`text-lg font-bold ${scoreColor}`}>
                          {item.confidence_score !== null ? item.confidence_score : '-'}
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-bold ${statusBadge.bg} ${statusBadge.text}`}>
                          {statusBadge.label}
                        </span>
                        <svg className="w-5 h-5 text-terminal-dim group-hover:text-status-go transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* 푸터 - 페이지네이션 */}
        {!isLoading && !isTabsLoading && items.length > 0 && (
          <div className="flex-shrink-0 border-t border-terminal-border px-6 py-3">
            <div className="flex items-center justify-between">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  currentPage <= 1
                    ? 'bg-terminal-dim/10 text-terminal-dim cursor-not-allowed'
                    : 'bg-terminal-dim/20 hover:bg-terminal-dim/30 text-terminal-text'
                }`}
              >
                {isKo ? '← 이전' : '← Prev'}
              </button>
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-terminal-muted">
                  {isKo 
                    ? `${currentPage} / ${totalPages} 페이지`
                    : `Page ${currentPage} of ${totalPages}`
                  }
                </span>
                <span className="text-xs text-terminal-dim">
                  ({isKo ? `총 ${total}건` : `${total} items`})
                </span>
              </div>
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  currentPage >= totalPages
                    ? 'bg-terminal-dim/10 text-terminal-dim cursor-not-allowed'
                    : 'bg-terminal-dim/20 hover:bg-terminal-dim/30 text-terminal-text'
                }`}
              >
                {isKo ? '다음 →' : 'Next →'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
